import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { uploadsApi } from '@/api/uploads';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { extractApiErrorMessage } from '@/lib/api-errors';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { captureFeatureBlocked } from '@/lib/posthog';
import { resolvePreviewUrl, setPreviewUrl } from '@/utils/preview-cache';
import { validateImageFile } from '@/utils/validate-image-file';
import type { FileUploadState } from '@/components/customize/types';
import type { ReportData } from '@/components/preview/types';
import type { BlockedSurface } from '@/lib/analytics-events';

type ReportUpdater = (
    updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>),
) => void;

interface UseBrandLogoOptions {
    data: ReportData;
    onUpdate: ReportUpdater;
    /** Which screen reports the entitlement block to analytics. */
    surface: BlockedSurface;
    /**
     * Runs after the asset ref is stored. The asset is shared, but "show it
     * here" is not: the capa flips `cover.show_logo`, the cabeçalho flips
     * `header.show_logo`.
     */
    onUploaded?: (assetRef: string) => void;
}

/**
 * The one place that owns the brand logo — the single asset in
 * `customizations.header.logo_url` that the capa, o cabeçalho and o rodapé all
 * read. Upload confirms to S3 and writes the ref; remove clears it everywhere.
 *
 * Visibility is deliberately *not* here: each surface hides the logo with its
 * own flag, and removing the asset is the only destructive action.
 */
export const useBrandLogo = ({ data, onUpdate, surface, onUploaded }: UseBrandLogoOptions) => {
    const logoAccess = useFeatureEntitlement('reports.logo');
    const [uploadState, setUploadState] = useState<FileUploadState>({
        status: 'idle',
        progress: 0,
    });

    const logoRef = data.customizations?.header?.logo_url;

    const setBrandLogoUrl = useCallback(
        (nextUrl: string | undefined) => {
            onUpdate((prev) => ({
                customizations: {
                    ...prev.customizations,
                    header: {
                        alignment: 'left',
                        ...prev.customizations?.header,
                        logo_url: nextUrl,
                    },
                },
            }));
        },
        [onUpdate],
    );

    const uploadLogo = useCallback(
        async (file: File) => {
            if (logoAccess.shouldBlock) {
                captureFeatureBlocked(surface, logoAccess.decision);
                toast.warning(getEntitlementBlockedMessage(logoAccess.decision));
                return;
            }

            const validationError = validateImageFile(file);
            if (validationError) {
                setUploadState({ status: 'error', progress: 0, error: validationError });
                return;
            }

            setUploadState({ status: 'uploading', progress: 0 });

            try {
                // The confirmed reference — not the staging key — is what gets
                // stored and what the preview is later resolved by.
                const assetRef = await uploadsApi.uploadAndConfirm({
                    file,
                    intent: 'logo',
                    onProgress: (progress) => setUploadState((prev) => ({ ...prev, progress })),
                });

                setPreviewUrl(assetRef, URL.createObjectURL(file));
                setBrandLogoUrl(assetRef);
                onUploaded?.(assetRef);
                setUploadState({ status: 'success', progress: 100 });
            } catch (err) {
                console.error('Logo upload failed', err);
                setUploadState({
                    status: 'error',
                    progress: 0,
                    // The backend can genuinely reject an image now (corrupt,
                    // oversized), and its pt-BR message says which.
                    error: extractApiErrorMessage(err, 'Falha no upload. Tente novamente.'),
                });
            }
        },
        [logoAccess.shouldBlock, logoAccess.decision, surface, setBrandLogoUrl, onUploaded],
    );

    const removeLogo = useCallback(() => {
        setBrandLogoUrl(undefined);
        setUploadState({ status: 'idle', progress: 0 });
    }, [setBrandLogoUrl]);

    return {
        logoAccess,
        logoRef,
        logoUrl: resolvePreviewUrl(logoRef),
        hasLogo: Boolean(logoRef),
        uploadState,
        uploadLogo,
        removeLogo,
        setBrandLogoUrl,
    };
};
